import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Search, Replace, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * FindReplaceModal - 찾기/바꾸기 모달 (docuai 포팅)
 * 
 * AUIGrid의 window.AUIGrid API를 직접 사용하여 셀 검색/치환을 수행합니다.
 * - 모두 찾기, 다음 찾기
 * - 바꾸기, 모두 바꾸기
 * - 대/소문자 구분, 전체 일치 옵션
 * - 결과 테이블 (클릭 시 셀 이동)
 * - 드래그 이동 가능
 */
const FindReplaceModal = ({ isOpen, onClose, gridId = '#horizontal_grid' }) => {
    const modalRef = useRef(null);
    const findInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    const [findText, setFindText] = useState('');
    const [replaceText, setReplaceText] = useState('');
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [exactMatch, setExactMatch] = useState(false);
    const [results, setResults] = useState([]);
    const [showReplace, setShowReplace] = useState(false);

    // 모달 열릴 때 초기화 + 포커스
    useEffect(() => {
        if (isOpen) {
            const w = 480;
            setPosition({
                x: Math.max(0, (window.innerWidth - w) / 2),
                y: Math.max(0, window.innerHeight / 4),
            });
            setTimeout(() => findInputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // 드래그 핸들
    const handleMouseDown = useCallback((e) => {
        if (e.target.closest('input, button, select, label')) return;
        e.preventDefault();
        setIsDragging(true);
        setOffset({
            x: e.clientX - (modalRef.current?.offsetLeft || 0),
            y: e.clientY - (modalRef.current?.offsetTop || 0),
        });
    }, []);

    useEffect(() => {
        if (!isDragging) return;
        const handleMove = (e) => {
            const modal = modalRef.current;
            if (!modal) return;
            let newX = e.clientX - offset.x;
            let newY = e.clientY - offset.y;
            newX = Math.max(0, Math.min(newX, window.innerWidth - modal.offsetWidth));
            newY = Math.max(0, Math.min(newY, window.innerHeight - modal.offsetHeight));
            setPosition({ x: newX, y: newY });
        };
        const handleUp = () => setIsDragging(false);
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
    }, [isDragging, offset]);

    // AUIGrid 헬퍼
    const getGrid = () => {
        if (!window.AUIGrid?.isCreated(gridId)) return null;
        return window.AUIGrid;
    };

    // ── 모두 찾기 ──
    const findAll = useCallback(() => {
        const grid = getGrid();
        if (!grid) { alert('그리드가 없습니다. 추출을 먼저 실행하세요.'); return; }

        const _find = caseSensitive ? findText : findText.toLowerCase();
        const colList = grid.getColumnLayout(gridId);
        const rowData = grid.getGridData(gridId);
        const found = [];

        for (let y = 0; y < rowData.length; y++) {
            for (let x = 0; x < colList.length; x++) {
                const field = colList[x].dataField;
                if (!field) continue;
                let cellVal = String(grid.getCellValue(gridId, y, field) ?? '');
                if (!caseSensitive) cellVal = cellVal.toLowerCase();

                const match = exactMatch ? cellVal === _find : (_find === '' ? cellVal === '' : cellVal.includes(_find));
                if (match) {
                    found.push({
                        row: y,
                        col: x,
                        field,
                        colName: colList[x].headerText || field,
                        value: String(grid.getCellValue(gridId, y, field) ?? ''),
                    });
                }
            }
        }
        setResults(found);
    }, [findText, caseSensitive, exactMatch, gridId]);

    // ── 다음 찾기 ──
    const findNext = useCallback(() => {
        const grid = getGrid();
        if (!grid) { alert('그리드가 없습니다.'); return; }

        const _find = caseSensitive ? findText : findText.toLowerCase();
        const colList = grid.getColumnLayout(gridId);
        const rowCount = grid.getRowCount(gridId);
        const colCount = colList.length;

        let selectedIndex;
        try { selectedIndex = grid.getSelectedIndex(gridId); } catch { selectedIndex = [-1, -1]; }
        let [nowY, nowX] = Array.isArray(selectedIndex) ? selectedIndex : [-1, -1];
        if (nowX === -1 && nowY === -1) { nowX = -1; nowY = 0; }

        // 다음 셀부터 탐색
        let x = nowX + 1;
        let y = nowY;
        while (y < rowCount) {
            while (x < colCount) {
                const field = colList[x].dataField;
                if (field) {
                    let cellVal = String(grid.getCellValue(gridId, y, field) ?? '');
                    if (!caseSensitive) cellVal = cellVal.toLowerCase();
                    const match = exactMatch ? cellVal === _find : (_find === '' ? cellVal === '' : cellVal.includes(_find));
                    if (match) {
                        grid.setSelectionByIndex(gridId, y, x);
                        return;
                    }
                }
                x++;
            }
            y++;
            x = 0;
        }
        alert('더 이상 찾을 데이터가 없습니다.');
    }, [findText, caseSensitive, exactMatch, gridId]);

    // ── 바꾸기 (선택 셀) ──
    const replaceCurrent = useCallback(() => {
        const grid = getGrid();
        if (!grid) return;

        const _find = caseSensitive ? findText : findText.toLowerCase();
        let selectedItems;
        try { selectedItems = grid.getSelectedItems(gridId); } catch { selectedItems = []; }

        let count = 0;
        selectedItems.forEach((item) => {
            let cellVal = String(item.value ?? '');
            const original = cellVal;
            if (!caseSensitive) cellVal = cellVal.toLowerCase();

            let matched = false;
            if (exactMatch) {
                matched = cellVal === _find;
            } else {
                matched = _find === '' ? cellVal === '' : cellVal.includes(_find);
            }

            if (matched) {
                let newText;
                if (exactMatch) {
                    newText = replaceText;
                } else {
                    const idx = cellVal.indexOf(_find);
                    newText = original.slice(0, idx) + replaceText + original.slice(idx + _find.length);
                }
                grid.setCellValue(gridId, item.rowIndex, item.dataField, newText);
                count++;
            }
        });

        if (count > 0) {
            findAll(); // 결과 새로고침
            findNext(); // 다음으로 이동
        }
    }, [findText, replaceText, caseSensitive, exactMatch, gridId, findAll, findNext]);

    // ── 모두 바꾸기 ──
    const replaceAll = useCallback(() => {
        const grid = getGrid();
        if (!grid) return;

        const _find = caseSensitive ? findText : findText.toLowerCase();
        const colList = grid.getColumnLayout(gridId);
        const rowData = grid.getGridData(gridId);
        let count = 0;

        for (let y = 0; y < rowData.length; y++) {
            for (let x = 0; x < colList.length; x++) {
                const field = colList[x].dataField;
                if (!field) continue;
                let cellVal = String(grid.getCellValue(gridId, y, field) ?? '');
                const original = cellVal;
                if (!caseSensitive) cellVal = cellVal.toLowerCase();

                let matched = false;
                if (exactMatch) {
                    matched = cellVal === _find;
                } else {
                    matched = _find === '' ? cellVal === '' : cellVal.includes(_find);
                }

                if (matched) {
                    let newText;
                    if (exactMatch) {
                        newText = replaceText;
                    } else {
                        const idx = cellVal.indexOf(_find);
                        newText = original.slice(0, idx) + replaceText + original.slice(idx + _find.length);
                    }
                    grid.setCellValue(gridId, y, field, newText);
                    count++;
                }
            }
        }
        setResults([]);
        alert(`${count}개의 셀을 변경하였습니다.`);
    }, [findText, replaceText, caseSensitive, exactMatch, gridId]);

    // ── 결과 클릭 → 셀 이동 ──
    const handleResultClick = (item) => {
        const grid = getGrid();
        if (!grid) return;
        grid.setSelectionByIndex(gridId, item.row, item.col);
    };

    // Enter 키로 다음 찾기
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            findNext();
        }
        if (e.key === 'Escape') {
            onClose();
        }
    };

    const closeModal = () => {
        setFindText('');
        setReplaceText('');
        setResults([]);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            ref={modalRef}
            style={{ left: position.x, top: position.y, position: 'fixed' }}
            className="z-[9999] w-[480px] bg-white rounded-lg shadow-2xl border border-gray-300"
        >
            {/* 헤더 (드래그 가능) */}
            <div
                className="flex items-center justify-between px-4 py-2.5 bg-gray-50 rounded-t-lg border-b border-gray-200 cursor-move select-none"
                onMouseDown={handleMouseDown}
            >
                <div className="flex items-center gap-2">
                    <Search size={15} className="text-[#3C4043]" />
                    <span className="text-sm font-semibold text-gray-800">찾기/바꾸기</span>
                </div>
                <button
                    onClick={closeModal}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                    <X size={14} className="text-gray-500" />
                </button>
            </div>

            <div className="p-4 space-y-3">
                {/* 찾기 입력 */}
                <div className="flex items-center gap-2">
                    <label className="text-[13px] text-gray-600 w-16 flex-shrink-0">찾을 내용</label>
                    <input
                        ref={findInputRef}
                        type="text"
                        value={findText}
                        onChange={(e) => setFindText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="flex-1 px-2.5 py-1.5 text-[13px] border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="검색어 입력..."
                    />
                    <button
                        onClick={findNext}
                        className="px-3 py-1.5 text-[13px] font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors text-gray-700"
                    >
                        다음
                    </button>
                    <button
                        onClick={findAll}
                        className="px-3 py-1.5 text-[13px] font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors text-gray-700"
                    >
                        모두 찾기
                    </button>
                </div>

                {/* 바꾸기 토글 + 입력 */}
                <div>
                    <button
                        onClick={() => setShowReplace(!showReplace)}
                        className="flex items-center gap-1 text-[13px] text-gray-500 hover:text-gray-700 mb-2"
                    >
                        {showReplace ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        바꾸기
                    </button>
                    {showReplace && (
                        <div className="flex items-center gap-2">
                            <label className="text-[13px] text-gray-600 w-16 flex-shrink-0">바꿀 내용</label>
                            <input
                                type="text"
                                value={replaceText}
                                onChange={(e) => setReplaceText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="flex-1 px-2.5 py-1.5 text-[13px] border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="바꿀 텍스트..."
                            />
                            <button
                                onClick={replaceCurrent}
                                className="px-3 py-1.5 text-[13px] font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors text-gray-700"
                            >
                                바꾸기
                            </button>
                            <button
                                onClick={replaceAll}
                                className="px-3 py-1.5 text-[13px] font-medium bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors text-gray-700"
                            >
                                모두 바꾸기
                            </button>
                        </div>
                    )}
                </div>

                {/* 옵션 */}
                <div className="flex items-center gap-4 pt-1">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={caseSensitive}
                            onChange={(e) => setCaseSensitive(e.target.checked)}
                            className="w-3.5 h-3.5 accent-blue-600 rounded"
                        />
                        <span className="text-[13px] text-gray-600">대/소문자 구분</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={exactMatch}
                            onChange={(e) => setExactMatch(e.target.checked)}
                            className="w-3.5 h-3.5 accent-blue-600 rounded"
                        />
                        <span className="text-[13px] text-gray-600">전체 내용 일치</span>
                    </label>
                </div>

                {/* 결과 테이블 */}
                {results.length > 0 && (
                    <div className="border-t border-gray-200 pt-2">
                        <div className="text-xs text-gray-500 mb-1.5">
                            <span className="font-semibold text-gray-700">{results.length}</span>개의 셀을 찾았습니다.
                        </div>
                        <div className="max-h-48 overflow-auto border border-gray-200 rounded">
                            <table className="w-full text-xs">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="text-left px-2 py-1.5 text-gray-600 font-medium border-b border-gray-200 w-12">행</th>
                                        <th className="text-left px-2 py-1.5 text-gray-600 font-medium border-b border-gray-200 w-24">열</th>
                                        <th className="text-left px-2 py-1.5 text-gray-600 font-medium border-b border-gray-200">값</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((item, idx) => (
                                        <tr
                                            key={`${item.row}_${item.col}_${idx}`}
                                            onClick={() => handleResultClick(item)}
                                            className="cursor-pointer hover:bg-blue-50 transition-colors"
                                        >
                                            <td className="px-2 py-1 border-b border-gray-100 text-gray-700">{item.row + 1}</td>
                                            <td className="px-2 py-1 border-b border-gray-100 text-gray-700">{item.colName}</td>
                                            <td className="px-2 py-1 border-b border-gray-100 text-gray-900 truncate max-w-[200px]">{item.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FindReplaceModal;
